import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import Sheet from "../../components/Sheet";
import { fmtSize, openDocument } from "../../lib/openDocument";
import { Building2, CalendarDays, Check, ExternalLink, FileText, Loader2, Plus, Search, Trash2, Upload } from "lucide-react";

const MAX_MB = 25;

const dateLabel = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" });
};
const baseName = (n) => {
  const s = String(n || "");
  const i = s.lastIndexOf(".");
  return (i > 0 ? s.slice(0, i) : s).split("_").join(" ").trim();
};
const isPdf = (f) => f.type === "application/pdf" || String(f.name || "").toLowerCase().endsWith(".pdf");

/* ---------- janela: novo formulario ---------- */

function NewFormSheet({ siteList, onClose, onDone }) {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [name, setName] = useState("");
  const [touched, setTouched] = useState(false);
  const [picked, setPicked] = useState([]);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState("");

  const onFile = (e) => {
    const f = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!f) return;
    if (!isPdf(f)) {
      setError("Choose a PDF file.");
      return;
    }
    if (f.size > MAX_MB * 1024 * 1024) {
      setError(`This PDF is too large (${MAX_MB} MB at most).`);
      return;
    }
    setError("");
    setFile(f);
    if (!touched) setName(baseName(f.name));
  };
  const toggle = (id) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const allPicked = siteList.length > 0 && picked.length === siteList.length;

  const submit = async () => {
    if (busy || !file || !name.trim()) return;
    setBusy(true);
    setError("");
    setProgress(0);
    try {
      const fd = new FormData();
      fd.append("file", file, file.name);
      fd.append("name", name.trim());
      fd.append("jobsite_ids", JSON.stringify(picked));
      await api.post("/forms/upload", fd, {
        timeout: 180000,
        onUploadProgress: (e) => {
          if (e && e.total) setProgress(Math.round((e.loaded * 100) / e.total));
        },
      });
      toast.success("Form added");
      onDone();
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail, "Could not upload the form."));
      setBusy(false);
    }
  };

  return (
    <Sheet
      title="New form"
      subtitle="A fillable PDF your crew can fill in and sign"
      onClose={busy ? () => {} : onClose}
      footer={
        <button
          type="button"
          onClick={submit}
          disabled={busy || !file || !name.trim()}
          className="flex h-14 w-full items-center justify-center gap-2.5 rounded-2xl bg-primary text-base font-bold text-primary-foreground transition-opacity active:opacity-90 disabled:opacity-50"
          data-testid="form-submit"
        >
          {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Upload className="h-5 w-5" />}
          {busy ? (progress > 0 && progress < 100 ? `Uploading ${progress}%` : "Uploading...") : "Add form"}
        </button>
      }
    >
      <div className="mb-1.5 text-xs font-bold uppercase tracking-wide text-muted-foreground">PDF file</div>
      <button
        type="button"
        onClick={() => fileRef.current && fileRef.current.click()}
        disabled={busy}
        className="flex w-full items-center gap-3.5 rounded-xl border border-dashed border-input p-3.5 text-left active:bg-accent disabled:opacity-60"
        data-testid="form-pick"
      >
        <span className="grid h-11 w-11 shrink-0 place-items-center rounded-lg bg-primary/10 text-primary">
          <FileText className="h-5 w-5" />
        </span>
        <span className="min-w-0 flex-1">
          {file ? (
            <>
              <span className="block truncate text-sm font-semibold" data-testid="form-file-name">
                {file.name}
              </span>
              <span className="text-xs text-muted-foreground">{fmtSize(file.size)} · tap to change</span>
            </>
          ) : (
            <>
              <span className="block text-sm font-semibold">Choose a PDF</span>
              <span className="text-xs text-muted-foreground">Up to {MAX_MB} MB</span>
            </>
          )}
        </span>
      </button>
      <input ref={fileRef} type="file" accept="application/pdf,.pdf" className="hidden" onChange={onFile} data-testid="form-file-input" />

      <label className="mb-1.5 mt-5 block text-xs font-bold uppercase tracking-wide text-muted-foreground" htmlFor="form-name">
        Name
      </label>
      <input
        id="form-name"
        value={name}
        onChange={(e) => {
          setName(e.target.value);
          setTouched(true);
        }}
        maxLength={120}
        disabled={busy}
        placeholder="e.g. Daily safety check"
        className="h-12 w-full rounded-xl border border-input bg-background px-3 text-base outline-none focus:border-primary disabled:opacity-60"
        data-testid="form-name"
      />

      <div className="mb-1.5 mt-5 flex items-center justify-between">
        <div className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Available on</div>
        {siteList.length > 1 && (
          <button type="button" disabled={busy} onClick={() => setPicked(allPicked ? [] : siteList.map((s) => s.id))} className="text-sm font-semibold text-primary" data-testid="form-all-sites">
            {allPicked ? "Clear" : "Select all"}
          </button>
        )}
      </div>
      {siteList.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border p-3 text-sm text-muted-foreground" data-testid="form-no-sites">
          There are no jobsites yet.
        </div>
      ) : (
        <div className="flex flex-wrap gap-2" data-testid="form-sites">
          {siteList.map((s) => {
            const on = picked.includes(s.id);
            return (
              <button
                key={s.id}
                type="button"
                disabled={busy}
                onClick={() => toggle(s.id)}
                aria-pressed={on}
                className={`flex h-11 max-w-full items-center gap-1.5 rounded-full border px-4 text-sm font-semibold transition-colors ${on ? "border-primary bg-primary/10 text-primary" : "border-border bg-card text-muted-foreground active:bg-accent"}`}
                data-testid={`form-site-${s.id}`}
              >
                {on ? <Check className="h-4 w-4 shrink-0" /> : null}
                <span className="truncate">{s.name}</span>
              </button>
            );
          })}
        </div>
      )}
      {error && (
        <div className="mt-4 rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-400" role="alert" data-testid="form-error">
          {error}
        </div>
      )}
    </Sheet>
  );
}

/* ---------- janela: apagar ---------- */

function DeleteSheet({ form, onClose, onDone }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const remove = async () => {
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      await api.delete(`/forms/${form.id}`);
      toast.success("Form deleted");
      onDone(form.id);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail, "Could not delete the form."));
      setBusy(false);
    }
  };
  return (
    <Sheet
      title="Delete this form?"
      subtitle={form.name}
      onClose={busy ? () => {} : onClose}
      footer={
        <div className="flex gap-2.5">
          <button type="button" onClick={onClose} disabled={busy} className="h-14 flex-1 rounded-2xl border border-border text-base font-semibold active:bg-accent disabled:opacity-50" data-testid="delete-cancel">
            Cancel
          </button>
          <button type="button" onClick={remove} disabled={busy} className="flex h-14 flex-1 items-center justify-center gap-2 rounded-2xl bg-red-600 text-base font-bold text-white active:opacity-90 disabled:opacity-60" data-testid="delete-confirm">
            {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Trash2 className="h-5 w-5" />} Delete
          </button>
        </div>
      }
    >
      <p className="text-sm text-muted-foreground">Your crew will no longer see this form. This cannot be undone.</p>
      {error && (
        <div className="mt-3 rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-400" role="alert" data-testid="delete-error">
          {error}
        </div>
      )}
    </Sheet>
  );
}

/* ---------- cartao de um formulario ---------- */

function FormCard({ form, siteNames, onOpen, onDelete }) {
  const names = (form.jobsite_ids || []).map((id) => siteNames[id]).filter(Boolean);
  const shown = names.slice(0, 3);
  const more = names.length - shown.length;
  return (
    <div className="rounded-2xl border border-border bg-card p-4" data-testid={`form-${form.id}`}>
      <div className="flex items-start gap-3.5">
        <div className="grid h-12 w-12 shrink-0 place-items-center rounded-xl bg-primary/10 text-primary">
          <FileText className="h-6 w-6" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="break-words text-base font-bold leading-snug" data-testid="form-title">
            {form.name || "Untitled form"}
          </div>
          {form.original_filename ? <div className="truncate text-xs text-muted-foreground">{form.original_filename}</div> : null}
        </div>
      </div>

      <div className="mt-3.5 space-y-2 text-sm">
        <div className="flex items-start gap-2 text-muted-foreground">
          <Building2 className="mt-0.5 h-4 w-4 shrink-0" />
          {names.length === 0 ? (
            <span className="text-amber-500" data-testid="form-nosites">
              No sites selected
            </span>
          ) : (
            <div className="flex flex-wrap gap-1.5" data-testid="form-sitechips">
              {shown.map((n) => (
                <span key={n} className="rounded-full border border-border px-2.5 py-0.5 text-xs font-medium text-foreground">
                  {n}
                </span>
              ))}
              {more > 0 && <span className="rounded-full border border-border px-2.5 py-0.5 text-xs font-medium text-muted-foreground">+{more} more</span>}
            </div>
          )}
        </div>
        {dateLabel(form.created_at) && (
          <div className="flex items-center gap-2 text-muted-foreground">
            <CalendarDays className="h-4 w-4 shrink-0" />
            <span>Added {dateLabel(form.created_at)}</span>
          </div>
        )}
      </div>

      <div className="mt-4 flex gap-2.5">
        <button type="button" onClick={() => onOpen(form)} className="flex h-12 flex-1 items-center justify-center gap-2 rounded-xl border border-border text-sm font-semibold active:bg-accent" data-testid={`form-open-${form.id}`}>
          <ExternalLink className="h-4 w-4" /> Open
        </button>
        <button type="button" onClick={() => onDelete(form)} aria-label={`Delete ${form.name || "form"}`} className="grid h-12 w-12 place-items-center rounded-xl border border-border text-red-400 active:bg-red-500/10" data-testid={`form-delete-${form.id}`}>
          <Trash2 className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
}

/* ---------- pagina ---------- */

export default function AdminForms() {
  const [forms, setForms] = useState(null);
  const [siteList, setSiteList] = useState([]);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [adding, setAdding] = useState(false);
  const [removing, setRemoving] = useState(null);

  const load = useCallback(async () => {
    try {
      const [f, s] = await Promise.all([api.get("/forms/admin/list"), api.get("/jobsites").catch(() => ({ data: [] }))]);
      const list = Array.isArray(f.data) ? f.data : [];
      setForms([...list].sort((a, b) => String(b.created_at || "").localeCompare(String(a.created_at || ""))));
      setSiteList(Array.isArray(s.data) ? s.data.map((x) => ({ id: x.id, name: x.name })) : []);
      setError("");
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail, "Could not load the forms."));
      setForms((x) => x || []);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const siteNames = useMemo(() => Object.fromEntries(siteList.map((s) => [s.id, s.name])), [siteList]);
  const q = query.trim().toLowerCase();
  const shown = (forms || []).filter((f) => !q || `${f.name || ""} ${f.original_filename || ""}`.toLowerCase().includes(q));

  return (
    <div className="mx-auto max-w-4xl px-4 pb-28 pt-4 md:px-6 md:pb-12 md:pt-8" data-testid="forms-page">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-sm text-muted-foreground">Administration</div>
          <h1 className="text-3xl font-bold leading-tight tracking-tight" data-testid="forms-title">
            Forms
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">Fillable PDFs your crew fills in and signs.</p>
        </div>
        <button type="button" onClick={() => setAdding(true)} className="mt-1 flex h-12 shrink-0 items-center gap-2 rounded-xl bg-primary px-4 text-sm font-bold text-primary-foreground active:opacity-90" data-testid="forms-new">
          <Plus className="h-5 w-5" /> New form
        </button>
      </div>

      {forms && forms.length > 0 && (
        <div className="relative mt-5">
          <Search className="pointer-events-none absolute left-3.5 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search forms"
            className="h-12 w-full rounded-xl border border-input bg-card pl-11 pr-3 text-base outline-none focus:border-primary"
            data-testid="forms-search"
          />
        </div>
      )}

      {error && (
        <div className="mt-4 flex items-center justify-between gap-3 rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-400" role="alert" data-testid="forms-error">
          <span>{error}</span>
          <button type="button" onClick={load} className="shrink-0 font-semibold underline" data-testid="forms-retry">
            Try again
          </button>
        </div>
      )}

      {forms === null ? (
        <div className="grid h-40 place-items-center text-muted-foreground" data-testid="forms-loading">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      ) : forms.length === 0 ? (
        !error && (
          <div className="mt-8 rounded-2xl border border-dashed border-border p-8 text-center" data-testid="forms-empty">
            <FileText className="mx-auto h-8 w-8 text-muted-foreground" />
            <div className="mt-3 text-lg font-bold">No forms yet</div>
            <div className="mx-auto mt-1 max-w-xs text-sm text-muted-foreground">Upload a PDF and your crew can fill it in and sign it from their phones.</div>
            <button type="button" onClick={() => setAdding(true)} className="mt-5 inline-flex h-12 items-center gap-2 rounded-xl bg-primary px-5 text-sm font-bold text-primary-foreground" data-testid="forms-empty-new">
              <Plus className="h-5 w-5" /> New form
            </button>
          </div>
        )
      ) : (
        <>
          <div className="mx-0.5 mb-2 mt-4 text-xs text-muted-foreground" data-testid="forms-count">
            {shown.length} {shown.length === 1 ? "form" : "forms"}
          </div>
          {shown.length === 0 ? (
            <div className="py-10 text-center text-sm text-muted-foreground" data-testid="forms-nomatch">
              No form matches your search.
            </div>
          ) : (
            <div className="grid gap-3 md:grid-cols-2" data-testid="forms-list">
              {shown.map((f) => (
                <FormCard key={f.id} form={f} siteNames={siteNames} onOpen={(x) => openDocument(x.file_url)} onDelete={setRemoving} />
              ))}
            </div>
          )}
        </>
      )}

      {adding && (
        <NewFormSheet
          siteList={siteList}
          onClose={() => setAdding(false)}
          onDone={() => {
            setAdding(false);
            load();
          }}
        />
      )}
      {removing && (
        <DeleteSheet
          form={removing}
          onClose={() => setRemoving(null)}
          onDone={(id) => {
            setRemoving(null);
            setForms((list) => (list || []).filter((x) => x.id !== id));
          }}
        />
      )}
    </div>
  );
}
